import React, { useEffect, useMemo, useState } from "react";

/**
 * High School Common Knowledge Quiz — Full Page Component
 * - Header (title/date/tags) on top
 * - Quiz card sits directly underneath the header (tight spacing)
 * - Namespaced CSS to avoid collisions (ckq-*)
 */

// ---------- Utilities ----------
function shuffle(array) {
  const a = [...array];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const BANK = [
  { id: "q-cell-powerhouse", category: "Biology", question: "Which organelle is known as the powerhouse of the cell?", choices: ["Nucleus", "Ribosome", "Mitochondrion", "Golgi apparatus"], answerIndex: 2 },
  { id: "q-photosynthesis", category: "Biology", question: "Photosynthesis primarily occurs in which organelle?", choices: ["Mitochondrion", "Chloroplast", "Lysosome", "Endoplasmic reticulum"], answerIndex: 1 },
  { id: "q-dna", category: "Biology", question: "DNA stands for…", choices: ["Deoxyribonucleic acid", "Dioxygen nucleic acid", "Deoxyribose neutral acid", "Double helix nucleic agent"], answerIndex: 0 },
  { id: "q-writer-rj", category: "Literature", question: "Who wrote ‘Romeo and Juliet’?", choices: ["Charles Dickens", "William Shakespeare", "Jane Austen", "Mark Twain"], answerIndex: 1 },
  { id: "q-haiku", category: "Literature", question: "Traditional haiku uses which syllable pattern?", choices: ["4–6–4", "5–7–5", "7–5–7", "6–8–6"], answerIndex: 1 },
  { id: "q-independence", category: "History", question: "In which year was the U.S. Declaration of Independence signed?", choices: ["1492", "1865", "1776", "1812"], answerIndex: 2 },
  { id: "q-coldwar", category: "History", question: "The Cold War was primarily between which two superpowers?", choices: ["United States and China", "United States and Soviet Union", "France and Germany", "United Kingdom and Japan"], answerIndex: 1 },
  { id: "q-amendments", category: "Civics", question: "How many amendments does the U.S. Constitution have?", choices: ["10", "23", "27", "35"], answerIndex: 2 },
  { id: "q-branches", category: "Civics", question: "How many branches are there in the U.S. federal government?", choices: ["2", "3", "4", "5"], answerIndex: 1 },
  { id: "q-capital-fr", category: "Geography", question: "What is the capital of France?", choices: ["Lyon", "Berlin", "Madrid", "Paris"], answerIndex: 3 },
  { id: "q-sahara", category: "Geography", question: "The Sahara Desert is on which continent?", choices: ["Asia", "Africa", "Australia", "South America"], answerIndex: 1 },
  { id: "q-largest-ocean", category: "Geography", question: "Which is the largest ocean on Earth?", choices: ["Atlantic", "Indian", "Pacific", "Arctic"], answerIndex: 2 },
  { id: "q-planet", category: "Astronomy", question: "Which planet is the largest in our solar system?", choices: ["Earth", "Saturn", "Jupiter", "Neptune"], answerIndex: 2 },
  { id: "q-einstein", category: "Physics", question: "Who proposed the equation E = mc²?", choices: ["Isaac Newton", "Albert Einstein", "Niels Bohr", "Marie Curie"], answerIndex: 1 },
  { id: "q-boiling", category: "Chemistry", question: "What is the boiling point of water at sea level (°C)?", choices: ["90", "95", "100", "110"], answerIndex: 2 },
  { id: "q-evaporation", category: "Science", question: "The change from liquid to gas at the surface is called…", choices: ["Condensation", "Sublimation", "Evaporation", "Deposition"], answerIndex: 2 },
  { id: "q-plantsgas", category: "Biology", question: "Which gas do plants absorb during photosynthesis?", choices: ["Nitrogen", "Oxygen", "Carbon dioxide", "Helium"], answerIndex: 2 },
  { id: "q-kidneys", category: "Anatomy", question: "Which human organ primarily filters blood to make urine?", choices: ["Liver", "Kidneys", "Lungs", "Pancreas"], answerIndex: 1 },
  { id: "q-troposphere", category: "Earth Science", question: "Weather happens in which layer of Earth’s atmosphere?", choices: ["Stratosphere", "Mesosphere", "Thermosphere", "Troposphere"], answerIndex: 3 },
  { id: "q-slope", category: "Math", question: "The slope between two points is given by…", choices: ["(x₂ − x₁)/(y₂ − y₁)", "(y₂ − y₁)/(x₂ − x₁)", "(x₁ + x₂)/(y₁ + y₂)", "(y₁ − y₂)/(x₁ − x₂)"], answerIndex: 1 },
  { id: "q-pythagoras", category: "Math", question: "The Pythagorean theorem applies to which kind of triangle?", choices: ["Acute", "Obtuse", "Right", "Isosceles"], answerIndex: 2 },
  { id: "q-binary", category: "Computer Science", question: "What is the binary representation of the decimal number 2?", choices: ["01", "10", "11", "100"], answerIndex: 1 },
  { id: "q-mona-lisa", category: "Art", question: "Who painted the Mona Lisa?", choices: ["Michelangelo", "Leonardo da Vinci", "Raphael", "Van Gogh"], answerIndex: 1 },
  { id: "q-mercury", category: "Chemistry", question: "Which metal is liquid at room temperature?", choices: ["Mercury", "Aluminum", "Iron", "Sodium"], answerIndex: 0 },
];

function buildQuiz(bank, count = 12) {
  const selected = shuffle(bank).slice(0, count);
  return selected.map((q) => {
    const correctText = q.choices[q.answerIndex];
    const shuffled = shuffle(q.choices);
    return { id: q.id, category: q.category, question: q.question, choices: shuffled, correct: correctText };
  });
}

export default function HighSchoolQuizPage() {
  const today = new Date();
  const dateStr = today.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

  return (
    <div className="ckq-page">
      <style>{pageCss}</style>
      <header className="ckq-page-header">
        <h1 className="ckq-page-title">High School Common Knowledge Quiz</h1>
        <div className="ckq-date">{dateStr}</div>
        <div className="ckq-tags">
          <span className="ckq-tag">education</span>
          <span className="ckq-tag">knowledge</span>
          <span className="ckq-tag">quiz</span>
        </div>
      </header>

      {/* Quiz card sits right under the header */}
      <CommonKnowledgeQuiz />
    </div>
  );
}

function CommonKnowledgeQuiz() {
  const [started, setStarted] = useState(true); // start immediately to show the first question under header
  const [quiz, setQuiz] = useState(() => buildQuiz(BANK, 12));
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const current = quiz[index];
  const total = quiz.length || 12;
  const answeredCount = Object.keys(answers).length;

  const score = useMemo(() => {
    if (!submitted) return 0;
    return quiz.reduce((acc, q) => (answers[q.id] === q.correct ? acc + 1 : acc), 0);
  }, [submitted, answers, quiz]);

  function restart() {
    setQuiz(buildQuiz(BANK, total));
    setAnswers({});
    setIndex(0);
    setSubmitted(false);
    setStarted(true);
  }

  function select(choice) {
    if (!current || submitted) return;
    setAnswers((a) => ({ ...a, [current.id]: choice }));
  }

  const next = () => setIndex((i) => Math.min(i + 1, total - 1));
  const prev = () => setIndex((i) => Math.max(i - 1, 0));

  function submit() {
    if (Object.keys(answers).length < total) {
      const firstUnanswered = quiz.findIndex((q) => !(q.id in answers));
      if (firstUnanswered >= 0) setIndex(firstUnanswered);
      return;
    }
    setSubmitted(true);
  }

  useEffect(() => {
    function onKey(e) {
      if (!started || submitted || !current) return;
      const map = { 1: 0, 2: 1, 3: 2, 4: 3 };
      if (map[e.key] !== undefined && current.choices[map[e.key]]) select(current.choices[map[e.key]]);
      if (e.key === "Enter" || e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [started, submitted, current]);

  return (
    <section className="ckq-root">
      <div className="ckq-card">
        {!submitted ? (
          <>
            <header className="ckq-header">
              <div className="ckq-row ckq-space">
                <span className="ckq-muted">Question {index + 1} of {total}</span>
                <span className="ckq-muted">Answered {answeredCount}/{total}</span>
              </div>
              <div className="ckq-progress"><div className="ckq-bar" style={{ width: `${((index + 1) / total) * 100}%` }} /></div>
            </header>

            {current && (
              <>
                <p className="ckq-question">{current.question}</p>
                <div className="ckq-answers">
                  {current.choices.map((c, i) => {
                    const selected = answers[current.id] === c;
                    return (
                      <button key={c} className={"ckq-answer " + (selected ? "ckq-selected" : "") } onClick={() => select(c)}>
                        <span className="ckq-index" aria-hidden>{i + 1}</span>
                        <span>{c}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="ckq-row ckq-space ckq-topgap">
                  <button className="ckq-btn" onClick={prev} disabled={index === 0}>Previous</button>
                  {index < total - 1 ? (
                    <button className="ckq-btn ckq-primary" onClick={next}>Next</button>
                  ) : (
                    <button className="ckq-btn ckq-success" onClick={submit}>Submit Quiz</button>
                  )}
                </div>
              </>
            )}
          </>
        ) : (
          <Results quiz={quiz} answers={answers} score={score} onRetake={restart} />
        )}
      </div>
    </section>
  );
}

function Results({ quiz, answers, score, onRetake }) {
  const total = quiz.length;
  const percent = Math.round((score / total) * 100);

  const byCategory = quiz.reduce((acc, q) => {
    acc[q.category] ||= { correct: 0, total: 0 };
    acc[q.category].total += 1;
    if (answers[q.id] === q.correct) acc[q.category].correct += 1;
    return acc;
  }, {});

  return (
    <div>
      <h2 className="ckq-title">Your Results</h2>
      <p className="ckq-muted">Score: <strong>{score}</strong> / {total} ({percent}%)</p>

      <div className="ckq-grid">
        {Object.entries(byCategory).map(([cat, s]) => (
          <div key={cat} className="ckq-mini">
            <div className="ckq-row ckq-space"><h3 className="ckq-subtitle">{cat}</h3><span className="ckq-muted">{s.correct}/{s.total}</span></div>
            <div className="ckq-progress ckq-small"><div className="ckq-bar ckq-success" style={{ width: `${(s.correct / s.total) * 100}%` }} /></div>
          </div>
        ))}
      </div>

      <details className="ckq-review">
        <summary className="ckq-subtitle">Review answers</summary>
        <ul className="ckq-review-list">
          {quiz.map((q) => {
            const chosen = answers[q.id];
            const ok = chosen === q.correct;
            return (
              <li key={q.id} className="ckq-review-item">
                <p className="ckq-question ckq-sm">{q.question}</p>
                <p className={ok ? "ckq-ok" : "ckq-bad"}>
                  Your answer: <strong>{chosen ?? "—"}</strong>
                  {!ok && <> • Correct: <strong>{q.correct}</strong></>}
                </p>
              </li>
            );
          })}
        </ul>
      </details>

      <div className="ckq-row"><button className="ckq-btn ckq-primary" onClick={onRetake}>Retake Quiz</button></div>
    </div>
  );
}

// ---------- Page + Quiz CSS (namespaced) ----------
const pageCss = `
  .ckq-page{max-width:980px;margin:0 auto;padding:24px 20px 40px;background:#f6f7fb;color:#0f172a;font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial}
  .ckq-page-header{margin:0 0 12px}
  .ckq-page-title{margin:0 0 6px;font-size:34px;font-weight:800}
  .ckq-date{color:#6b7280;margin-bottom:8px}
  .ckq-tags{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:6px}
  .ckq-tag{background:#e5e7eb;color:#374151;border-radius:999px;font-size:12px;padding:4px 10px}

  /* quiz container sits right below header */
  .ckq-root{margin-top:6px}
  .ckq-card{background:#fff;border:1px solid #e5e7eb;border-radius:16px;box-shadow:0 8px 28px rgba(0,0,0,.06);padding:20px}

  .ckq-title{margin:0 0 8px;font-size:24px;font-weight:800}
  .ckq-subtitle{margin:0;font-size:16px;font-weight:700}
  .ckq-muted{color:#6b7280}
  .ckq-header{margin-bottom:12px}
  .ckq-row{display:flex;gap:12px;align-items:center}
  .ckq-space{justify-content:space-between}
  .ckq-question{font-size:20px;font-weight:700;margin:8px 0 12px}
  .ckq-sm{font-size:16px;font-weight:600}
  .ckq-answers{display:grid;grid-template-columns:repeat(1,minmax(0,1fr));gap:12px}
  @media(min-width:640px){.ckq-answers{grid-template-columns:repeat(2,minmax(0,1fr));}}
  .ckq-answer{display:flex;gap:12px;align-items:center;text-align:left;padding:14px 16px;border:1px solid #e5e7eb;border-radius:14px;background:#fff;transition:all .18s ease;cursor:pointer}
  .ckq-answer:hover{background:#f1f5f9}
  .ckq-selected{background:#eef2ff;border-color:#6366f1;box-shadow:0 0 0 4px rgba(99,102,241,.15)}
  .ckq-index{display:inline-flex;width:28px;height:28px;border-radius:999px;border:1px solid #e5e7eb;align-items:center;justify-content:center;font-size:14px;font-weight:600;color:#6b7280;background:#fff}
  .ckq-progress{height:10px;background:#e6ebf2;border-radius:999px;overflow:hidden}
  .ckq-small{height:8px}
  .ckq-bar{height:100%;background:linear-gradient(90deg,#60a5fa,#2563eb)}
  .ckq-success{background:linear-gradient(90deg,#34d399,#059669)}
  .ckq-topgap{margin-top:14px}
  .ckq-btn{appearance:none;border:1px solid #e5e7eb;background:#fff;color:#111827;padding:10px 16px;border-radius:12px;font-weight:600;cursor:pointer;transition:.18s;box-shadow:0 1px 0 rgba(0,0,0,.02)}
  .ckq-btn:hover{background:#f8fafc}
  .ckq-btn:disabled{opacity:.55;cursor:not-allowed}
  .ckq-primary{background:#2563eb;color:#fff;border-color:#2563eb}
  .ckq-primary:hover{background:#1d4ed8}
  .ckq-success{background:#059669;color:#fff;border-color:#059669}
  .ckq-grid{display:grid;gap:12px;grid-template-columns:repeat(1,minmax(0,1fr));}
  @media(min-width:640px){.ckq-grid{grid-template-columns:repeat(2,minmax(0,1fr));}}
  .ckq-mini{background:#fff;border:1px solid #e5e7eb;border-radius:12px;padding:12px}
  .ckq-review{margin:16px 0}
  .ckq-review-list{list-style:none;margin:12px 0 0;padding:0;display:grid;gap:10px}
  .ckq-review-item{border:1px solid #e5e7eb;border-radius:12px;padding:12px}
  .ckq-ok{color:#059669}
  .ckq-bad{color:#dc2626}
`;
